import { areasToFlatCollection, buildTagMatchRegExp } from '@/modules/floorplanDesign/helpers';
import { useFloorplanDesignStore } from '@/modules/floorplanDesign/store/useFloorplanDesignStore';
import { IFloorplanServiceConnector } from '@/modules/api/FloorplanServiceConnector/IFloorplanServiceConnector';
import { FloorplanServiceConnector } from '@/modules/api/FloorplanServiceConnector/FloorplanServiceConnector';
import { IErrorMessageServiceConnector } from '@/modules/api/ErrorMessageServiceConnector/IErrorMessageServiceConnector';
import { ErrorMessageServiceConnector } from '@/modules/api/ErrorMessageServiceConnector/ErrorMessageServiceConnector';
import { ISnackbarService } from '@/modules/snackbar/service/ISnackbarService';
import { SnackbarServiceFactory } from '@/modules/snackbar/service/SnackbarServiceFactory';
import { INotificationService } from '@/modules/Notifications/service/INotificationService';
import { NotificationServiceFactory } from '@/modules/Notifications/service/NotificationServiceFactory';
import { ICanvasService } from '@/modules/canvas/services/Canvas/ICanvasService';
import { CanvasServiceFactory } from '@/modules/canvas/services/Canvas/CanvasServiceFactory';
import { FddmServiceFactory } from '@/modules/canvas/services/Floorplan/FddmServiceFactory';
import { IFddmService } from '@/modules/canvas/services/Floorplan/IFddmService';
import { downloadFile } from '@/modules/common/helpers/browser';
import { FileServiceConnector, IFileServiceConnector } from '@/modules/api/FileApiConnector';
import { FDDM_TAG } from '@/modules/floorplanDesign/constants';
import { logger } from '@common/logging/logger';
import { PROJECT_FILES_ROOT_CONTAINER } from '@/modules/api/constant';

import { formatNotificationMessage } from '../helpers';
import { useFpsStore } from '../store/useFpsStore';
import { Status, Step } from '../types';
import { IFpsService } from './IFpsService';

export class FpsServiceImpl implements IFpsService {
  private fpsStore: typeof useFpsStore;
  private fddmStore: typeof useFloorplanDesignStore;
  private fddmSvc: IFddmService;
  private canvasSvc: ICanvasService;
  private notificationSvc: INotificationService;
  private snackbarSvc: ISnackbarService;
  private fpsConnector: IFloorplanServiceConnector;
  private emsConnector: IErrorMessageServiceConnector;
  private fileSvc: IFileServiceConnector;

  public constructor(fpsStore: typeof useFpsStore, fddmStore: typeof useFloorplanDesignStore) {
    this.fpsStore = fpsStore;
    this.fddmStore = fddmStore;
    this.fddmSvc = FddmServiceFactory.getInstance().createFddmService();
    this.canvasSvc = CanvasServiceFactory.getInstance().createCanvasService();
    this.notificationSvc = NotificationServiceFactory.getInstance().createNotificationService();
    this.snackbarSvc = SnackbarServiceFactory.getInstance().createSnackbarService();
    this.fpsConnector = FloorplanServiceConnector.getInstance();
    this.emsConnector = ErrorMessageServiceConnector.getInstance();
    this.fileSvc = FileServiceConnector.getInstance();
  }

  public async validateFloorplan(): Promise<void> {
    this.setStatus(Status.WaitingForResponse);

    const floorplanRevision = this.fddmStore.getState().getModel();

    try {
      const response = await this.fpsConnector.validateFloorplan(floorplanRevision);

      if (!response?.floorplan) {
        throw new Error('No floorplan returned from validation');
      }

      this.fddmSvc.setFloorplan(response.floorplan);

      const notifications = response.notifications ?? [];

      if (notifications.length === 0) {
        this.snackbarSvc.showMessage('Validation passed');
        this.setStep(Step.Tracks);
        return;
      }

      this.snackbarSvc.showMessage('Validation failed');

      const seenWarnings = new Set<string>();

      const uniqueNotifications = notifications.filter((notification) => {
        if (notification.arguments.length <= 1) {
          return true;
        }

        const uniqueKey = `${notification.code}-${notification.arguments[1]}`;

        if (seenWarnings.has(uniqueKey)) {
          return false;
        }

        seenWarnings.add(uniqueKey);
        return true;
      });

      const areaMap = new Map(
        areasToFlatCollection(floorplanRevision.design.areas).map((area) => [area.id, area.name]),
      );

      const notificationDetails = await Promise.all(
        uniqueNotifications.map(async (notification) => {
          const detailedNotification = await this.emsConnector.getNotificationDetails(
            notification.code,
          );

          return formatNotificationMessage(
            notification.arguments,
            detailedNotification,
            areaMap,
            async () => {
              this.notificationSvc.setPanelState(false);
              this.canvasSvc.zoomToFitElements(
                notification.arguments.filter((argument) => areaMap.has(argument)),
              );
              await new Promise<void>((resolve) => {
                setTimeout(() => resolve(), 100);
              }).then(() => {
                this.canvasSvc.toggleElementsVisible(
                  buildTagMatchRegExp([
                    FDDM_TAG.BUBBLE,
                    FDDM_TAG.BUBBLE_ARROW,
                    FDDM_TAG.BUBBLE_DIVIDER,
                    FDDM_TAG.CROSSING,
                    FDDM_TAG.DISTANT_CONNECTION,
                  ]),
                  true,
                );
              });
            },
          );
        }),
      );

      notificationDetails.forEach((notification) => {
        this.notificationSvc.addNotification(notification);
      });

      this.notificationSvc.setPanelState(true);
    } catch (error: unknown) {
      logger.error('Error validating floorplan:', error);
      this.snackbarSvc.showMessage('Validation failed');
    } finally {
      this.setStatus(Status.Idle);
    }
  }

  public async generateTracks(): Promise<void> {
    this.setStatus(Status.WaitingForResponse);

    try {
      const response = await this.fpsConnector.generateTracks(this.fddmStore.getState().getModel());

      if (!response || !response.tracks) {
        logger.error('Error generating tracks:', response);
        this.snackbarSvc.showMessage('Track generation failed');
        this.setStatus(Status.Idle);
        return;
      }

      this.fddmSvc.setTracks(response.tracks);
      this.snackbarSvc.showMessage('Tracks generated');
      this.setStep(Step.Export);
      this.setStatus(Status.Idle);
    } catch (error: unknown) {
      logger.error('Error generating tracks:', error);
      this.snackbarSvc.showMessage('Track generation failed');
      this.setStatus(Status.Idle);
    }
  }

  public async exportToRuntimeStack(
    generateTracks: boolean,
    generateProximityMatrix: boolean,
    generateTravelDurationCostMatrix: boolean,
  ): Promise<void> {
    this.setStatus(Status.WaitingForResponse);

    try {
      const response = await this.fpsConnector.exportToRuntimeStack(
        this.fddmStore.getState().getModel(),
        generateTracks,
        generateProximityMatrix,
        generateTravelDurationCostMatrix,
      );

      if (!response.outputZipFileRef) {
        logger.error('Error exporting files:', response);
        this.snackbarSvc.showMessage('Export failed');
        this.setStatus(Status.Idle);
        return;
      }

      const {
        metaData: { projectId },
      } = this.fddmStore.getState();

      const file = await this.fileSvc.getBlobFileByUrl(
        `${PROJECT_FILES_ROOT_CONTAINER}?filename=${projectId}/${response.outputZipFileRef}`,
      );
      const fileName = this.getExportFileName(response.outputZipFileRef);
      downloadFile(file, 'application/zip', fileName);

      this.snackbarSvc.showMessage('Export successful');
      this.setStatus(Status.Idle);
    } catch (error: unknown) {
      logger.error('Error exporting files:', error);
      this.snackbarSvc.showMessage('Export failed');
      this.setStatus(Status.Idle);
    }
  }

  public setStatus(status: Status): void {
    this.fpsStore.getState().setStatus(status);
  }

  public setStep(step: Step): void {
    this.fpsStore.getState().setStep(step);
  }

  public reset(): void {
    this.removeTracks();
    this.fpsStore.getState().reset();
  }

  private removeTracks(): void {
    const tracks = this.fddmSvc.getTracks();
    if (!tracks || tracks.length === 0) return;

    this.fddmSvc.setTracks([]);
    this.canvasSvc.removeCanvasElements({
      method: 'byTag',
      tags: [FDDM_TAG.TRACK_PIECE, FDDM_TAG.SWEPT_PATH],
    });
  }

  private getExportFileName(outputZipFileRef: string): string {
    try {
      const url = new URL(outputZipFileRef, window.location.origin);
      const fileNameFromQuery =
        url.searchParams.get('filename') || url.searchParams.get('fileName');

      if (fileNameFromQuery && fileNameFromQuery.trim().length > 0) {
        return fileNameFromQuery;
      }

      const pathNameParts = url.pathname.split('/').filter((item) => item.length > 0);
      const pathFileName = pathNameParts[pathNameParts.length - 1];

      if (pathFileName) {
        return decodeURIComponent(pathFileName);
      }
    } catch {
      // If URL parsing fails, use fallback.
    }

    return 'export.zip';
  }
}
